import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ShieldCheck,
  Users,
  MessageCircle,
} from "lucide-react";
import usePageMeta from "../../lib/usePageMeta";
import BreadcrumbSchema from "../../components/public/BreadcrumbSchema";
import AccessModelCallout from "../../components/public/AccessModelCallout";

/**
 * Public — Leadership / Employee Support service page.
 * Route: /services/leadership-employee-support
 *
 * IMAGE ASSETS
 * Local (not hotlinked). Sources documented in the report.
 */
const IMAGES = {
  hero: {
    src: "/images/leadership-employee-support/hero.jpg",
    alt: "Two adults in professional attire in a quiet, attentive one-on-one conversation, one listening thoughtfully.",
  },
  hrSupport: {
    src: "/images/leadership-employee-support/hr-support.jpg",
    alt: "A supportive workplace conversation between two colleagues in an open office, one leaning in to listen.",
  },
};

const LEADER_TOPICS = [
  "Decision fatigue",
  "Organizational change",
  "Difficult personnel situations",
  "Leadership pressure",
  "Family concerns",
  "Grief and loss",
  "Serious illness",
  "Caregiving responsibilities",
  "Financial stress",
  "Relationship strain",
  "Workplace conflict",
  "Ethical or values-based concerns",
  "Spiritual questions",
  "Simply needing to speak freely without managing everyone else's response",
];

const EMPLOYEE_CONCERNS = [
  "Grief and bereavement",
  "Family and relationship concerns",
  "Caregiving pressure",
  "Serious illness",
  "Personal stress",
  "Workplace transitions",
  "Conflict",
  "Financial concerns",
  "Loneliness",
  "Spiritual or meaning-of-life questions",
  "Major life changes",
  "Difficult news",
  "Emotional strain related to work",
  "Other appropriate personal concerns within chaplain scope",
];

const ACCESS_PATHWAYS = [
  "Scheduled on-site chaplain presence",
  "Phone",
  "Text",
  "Confidential message",
  "Available remote connection",
  "CALLACHAP",
  "The CCCHAPS Care Portal",
  "CCCHAPS On Demand",
  "24/7 on-call chaplain access",
];

const COMPLEMENTS = [
  "Human Resources",
  "Managers or supervisors",
  "Employee Assistance Programs",
  "Licensed mental-health professionals",
  "Medical providers",
  "Legal counsel",
  "Employee wellness programs",
  "Emergency services",
  "An employee's own clergy or faith community",
];

const AUDIENCE = [
  "Founders and business owners",
  "Executives",
  "HR and People leaders",
  "Managers and supervisors",
  "Administrative professionals",
  "Frontline employees",
  "Remote and field employees",
  "Hourly and shift-based workers",
  "Other eligible employees within the organization's CCCHAPS arrangement",
];

function SectionHeading({ eyebrow, children }) {
  return (
    <div className="mb-6">
      {eyebrow && (
        <p className="text-xs uppercase tracking-[0.2em] text-[#4A6FA5] font-semibold mb-3">
          {eyebrow}
        </p>
      )}
      <h2 className="font-heading text-2xl sm:text-3xl tracking-tight text-slate-900">
        {children}
      </h2>
    </div>
  );
}

export default function LeadershipEmployeeSupportPage() {
  usePageMeta({
    title: "Leadership / Employee Support | California Corporate Chaplains",
    description:
      "Confidential, non-clinical chaplain support for executives, HR and People leaders, managers, supervisors, and employees across the organization. 24/7 on-call chaplain access included.",
  });

  return (
    <article className="bg-white" data-testid="service-detail-leadership-employee-support">
      <BreadcrumbSchema
        id="leadership-employee-support"
        items={[
          { name: "Home", path: "/" },
          { name: "Services", path: "/services" },
          { name: "Leadership / Employee Support", path: "/services/leadership-employee-support" },
        ]}
      />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 md:pt-14 pb-10 md:pb-16">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#4A6FA5] mb-8"
            data-testid="les-back-to-services"
          >
            <ArrowLeft className="w-4 h-4" /> All services
          </Link>

          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            <div className="lg:col-span-7">
              <p className="text-xs uppercase tracking-[0.2em] text-[#4A6FA5] font-semibold mb-3">
                Service
              </p>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight text-slate-900 leading-[1.05]">
                Leadership / Employee Support
              </h1>
              <p className="mt-5 text-xl sm:text-2xl text-slate-700 font-heading leading-snug">
                Confidential Support for the People Who Carry Responsibility
              </p>

              <div className="mt-8 space-y-5 text-base sm:text-lg text-slate-700 leading-relaxed">
                <p>Every workplace has people others depend on.</p>
                <p>
                  Executives make difficult decisions. Managers carry the
                  pressure of leading teams. HR and People leaders often become
                  the first call when an employee is struggling. Employees
                  themselves may be balancing work demands with grief, family
                  concerns, caregiving, financial pressure, relationship
                  issues, health concerns, and other personal challenges.
                </p>
                <p>
                  California Corporate Chaplains provides confidential,
                  non-clinical 1:1 support across the organization — from
                  founders and senior leaders to frontline employees.
                </p>
                <p>
                  The purpose is simple: to give people a trusted place
                  outside the normal reporting structure where they can talk
                  openly, think out loud, process what they are carrying, and
                  receive appropriate support without turning the conversation
                  into a performance issue, disciplinary process, or clinical
                  encounter.
                </p>
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/schedule?type=employee_support"
                  data-testid="les-hero-cta-primary"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B]"
                >
                  Request Leadership or Employee Support{" "}
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/packages"
                  data-testid="les-hero-cta-secondary"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg border border-slate-300 text-slate-800 font-medium hover:border-[#4A6FA5] hover:text-[#4A6FA5]"
                >
                  Build a Care Package
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div
                className="aspect-[5/6] rounded-2xl overflow-hidden shadow-sm border border-slate-200 bg-slate-100"
                data-testid="les-image-slot-hero"
              >
                <img
                  src={IMAGES.hero.src}
                  alt={IMAGES.hero.alt}
                  loading="eager"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        {/* Leaders Need Somewhere to Turn Too */}
        <section className="mt-4 md:mt-6">
          <SectionHeading eyebrow="Leaders">
            Leaders Need Somewhere to Turn Too
          </SectionHeading>
          <div className="space-y-5 text-base text-slate-700 leading-relaxed mb-6">
            <p>Leadership can be isolating.</p>
            <p>
              There are conversations an executive may not want to have with
              employees, a manager may not be able to have with the team, and
              an HR leader may not feel comfortable placing inside a normal
              workplace relationship.
            </p>
            <p>That may include:</p>
          </div>
          <ul
            className="grid sm:grid-cols-2 gap-x-6 gap-y-3"
            data-testid="les-leader-topics"
          >
            {LEADER_TOPICS.map((t) => (
              <li key={t} className="flex items-start gap-3 text-slate-700">
                <Check
                  className="w-5 h-5 text-[#4A6FA5] shrink-0 mt-0.5"
                  strokeWidth={2}
                />
                <span className="leading-relaxed">{t}</span>
              </li>
            ))}
          </ul>
          <div className="mt-8 space-y-4 text-base text-slate-700 leading-relaxed">
            <p>
              A CCCHAPS chaplain provides confidential space for that
              conversation.
            </p>
            <p>
              The chaplain does not make leadership decisions for the
              individual and does not act as a business consultant or
              therapist.
            </p>
            <p>
              The role is to listen, provide perspective within professional
              chaplain scope, support the person behind the position, and help
              identify appropriate next steps or resources when needed.
            </p>
          </div>
        </section>

        {/* Supporting HR + image */}
        <section className="mt-16 md:mt-20">
          <SectionHeading eyebrow="HR & People leaders">
            Supporting HR and People Leaders Without Replacing Their Role
          </SectionHeading>
          <div className="grid md:grid-cols-5 gap-8 items-start">
            <div className="md:col-span-3 space-y-5 text-base text-slate-700 leading-relaxed">
              <p>
                HR professionals and People leaders regularly encounter
                employees during difficult moments.
              </p>
              <p>An employee may disclose a death in the family.</p>
              <p>A manager may be struggling personally.</p>
              <p>A team may be dealing with conflict.</p>
              <p>
                Someone may be overwhelmed by caregiving or a serious
                diagnosis.
              </p>
              <p>
                A personal crisis may begin affecting an employee's ability
                to navigate the workday.
              </p>
              <p>
                HR can respond appropriately to workplace responsibilities,
                policy, benefits, accommodations, and organizational
                processes — but HR should not have to become the employee's
                counselor, pastor, or sole emotional support.
              </p>
              <p>Workplace chaplaincy creates another pathway.</p>
              <p>
                A CCCHAPS chaplain can provide confidential, permission-based
                support outside the chain of command while HR maintains its
                proper organizational role.
              </p>
              <p className="font-medium text-slate-900">
                That distinction protects both the employee and the
                organization.
              </p>
            </div>
            <div className="md:col-span-2">
              <div
                className="aspect-[4/5] rounded-2xl overflow-hidden shadow-sm border border-slate-200 bg-slate-100"
                data-testid="les-image-slot-hr-support"
              >
                <img
                  src={IMAGES.hrSupport.src}
                  alt={IMAGES.hrSupport.alt}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Neutral resource */}
        <section className="mt-16 md:mt-20">
          <SectionHeading eyebrow="Neutral by design">
            A Neutral Resource Outside the Reporting Line
          </SectionHeading>
          <div className="space-y-5 text-base text-slate-700 leading-relaxed">
            <p>
              One of the most important characteristics of workplace
              chaplaincy is that the chaplain is not the employee's
              supervisor.
            </p>
            <p>The chaplain is not evaluating performance.</p>
            <p>The chaplain is not deciding promotions.</p>
            <p>The chaplain is not conducting discipline.</p>
            <p>
              The chaplain is not creating a clinical treatment record.
            </p>
            <p>
              That separation can make it easier for an employee to talk about
              what is happening without feeling that every personal concern is
              becoming an employment matter.
            </p>
            <p>Employees decide whether they want to engage.</p>
            <p>If they decline, that decision is respected.</p>
            <p>
              If they choose to talk, the conversation remains confidential
              within appropriate professional, ethical, safety, and legal
              limits.
            </p>
          </div>
        </section>

        {/* Support Across Roles and Shifts */}
        <section className="mt-16 md:mt-20">
          <SectionHeading eyebrow="Every role">
            Support for Employees Across Roles and Shifts
          </SectionHeading>
          <div className="space-y-5 text-base text-slate-700 leading-relaxed mb-6">
            <p>
              Personal challenges do not affect only executives or office
              employees.
            </p>
            <p>
              Frontline workers, field employees, supervisors, administrative
              teams, hourly employees, remote workers, and employees working
              nights or irregular shifts may all encounter periods when they
              need somewhere appropriate to turn.
            </p>
            <p>
              CCCHAPS chaplains can support employees around concerns such as:
            </p>
          </div>
          <ul
            className="grid sm:grid-cols-2 gap-x-6 gap-y-3"
            data-testid="les-employee-concerns"
          >
            {EMPLOYEE_CONCERNS.map((t) => (
              <li key={t} className="flex items-start gap-3 text-slate-700">
                <Check
                  className="w-5 h-5 text-[#4A6FA5] shrink-0 mt-0.5"
                  strokeWidth={2}
                />
                <span className="leading-relaxed">{t}</span>
              </li>
            ))}
          </ul>
          <div className="mt-8 space-y-3 text-base text-slate-700 leading-relaxed">
            <p>The chaplain's role is not to diagnose or treat.</p>
            <p>The first responsibility is often simply to listen well.</p>
          </div>
        </section>

        {/* What Confidential Chaplain Support Can Look Like */}
        <section className="mt-16 md:mt-20">
          <SectionHeading eyebrow="Access pathways">
            What Confidential Chaplain Support Can Look Like
          </SectionHeading>
          <div className="space-y-5 text-base text-slate-700 leading-relaxed mb-6">
            <p>
              Chaplain support may happen during a scheduled workplace visit
              or through one of the support pathways included in the
              organization's CCCHAPS arrangement.
            </p>
            <p>
              Depending on the Care Team Package and organization, employees
              and leaders may have access through:
            </p>
          </div>
          <ul
            className="grid sm:grid-cols-2 gap-x-6 gap-y-3"
            data-testid="les-access-pathways"
          >
            {ACCESS_PATHWAYS.map((t) => (
              <li key={t} className="flex items-start gap-3 text-slate-700">
                <MessageCircle
                  className="w-5 h-5 text-[#4A6FA5] shrink-0 mt-0.5"
                  strokeWidth={1.75}
                />
                <span className="leading-relaxed">{t}</span>
              </li>
            ))}
          </ul>
          <p className="mt-8 text-base text-slate-700 leading-relaxed">
            This gives employees more than one way to reach support and helps
            make care available beyond a single scheduled workplace visit.
          </p>

          <AccessModelCallout
            variant="leadership"
            className="mt-8"
            testId="les-access-model-callout"
          />
        </section>

        {/* Permission & Trust */}
        <section className="mt-16 md:mt-20">
          <SectionHeading eyebrow="Permission & trust">
            Care Built on Permission and Trust
          </SectionHeading>
          <div className="space-y-5 text-base text-slate-700 leading-relaxed">
            <p>CCCHAPS chaplaincy is voluntary.</p>
            <p>
              The individual controls whether a conversation happens and what
              they choose to discuss.
            </p>
            <p>
              Spiritual care is offered when welcomed and never imposed.
            </p>
            <p>
              Employees do not have to hold a particular religious belief to
              speak with a chaplain.
            </p>
            <p>Support is respectful of belief and non-belief.</p>
            <p>
              Confidentiality is maintained within appropriate professional,
              ethical, safety, and legal limits.
            </p>
            <p>
              These boundaries are essential because trust cannot be built
              when employees believe personal conversations will automatically
              become organizational information.
            </p>
          </div>
        </section>

        {/* Organization sees / doesn't see */}
        <section className="mt-16 md:mt-20">
          <SectionHeading eyebrow="Privacy & accountability">
            What the Organization Sees — and What It Does Not
          </SectionHeading>
          <div className="space-y-5 text-base text-slate-700 leading-relaxed">
            <p>
              CCCHAPS maintains a clear distinction between individual care
              and organizational accountability.
            </p>
            <p>
              Organizations may receive appropriate aggregate,
              non-identifying information about service utilization and areas
              of support.
            </p>
            <p>
              They do not receive the private details of an employee's
              chaplain conversation.
            </p>
          </div>
          <div className="mt-8 rounded-2xl bg-[#1F3A5F] text-white p-8 md:p-10 shadow-md flex items-start gap-4">
            <ShieldCheck
              className="w-8 h-8 shrink-0 mt-1 opacity-90"
              strokeWidth={1.75}
            />
            <p className="font-heading text-xl sm:text-2xl leading-snug tracking-tight">
              The organization sees the impact. The employee keeps the
              conversation.
            </p>
          </div>
          <p className="mt-6 text-base text-slate-700 leading-relaxed">
            This allows organizations to understand whether their care
            resource is being used while protecting individual privacy.
          </p>
        </section>

        {/* Working alongside */}
        <section className="mt-16 md:mt-20">
          <SectionHeading eyebrow="Complementary care">
            Working Alongside HR, EAP, and Professional Care
          </SectionHeading>
          <p className="text-base text-slate-700 leading-relaxed mb-6">
            CCCHAPS does not replace:
          </p>
          <ul
            className="grid sm:grid-cols-2 gap-x-6 gap-y-3"
            data-testid="les-complements"
          >
            {COMPLEMENTS.map((t) => (
              <li key={t} className="flex items-start gap-3 text-slate-700">
                <Check
                  className="w-5 h-5 text-[#4A6FA5] shrink-0 mt-0.5"
                  strokeWidth={2}
                />
                <span className="leading-relaxed">{t}</span>
              </li>
            ))}
          </ul>
          <div className="mt-8 space-y-3 text-base text-slate-700 leading-relaxed">
            <p>Chaplaincy provides an additional relational layer of support.</p>
            <p>
              When a concern falls outside chaplain scope, the chaplain can
              encourage the individual to connect with the appropriate
              professional, organizational, community, or emergency resource.
            </p>
          </div>
        </section>

        {/* When work and life collide */}
        <section className="mt-16 md:mt-20">
          <SectionHeading eyebrow="Work & life">
            When Work and Life Collide
          </SectionHeading>
          <div className="space-y-5 text-base text-slate-700 leading-relaxed">
            <p>
              Employees do not leave the rest of life at the workplace door.
            </p>
            <p>
              A family diagnosis, marriage difficulty, death, financial
              crisis, caregiving responsibility, spiritual struggle, or major
              life transition may follow someone into the workday even when
              they continue showing up and doing their job.
            </p>
            <p>Likewise, workplace pressures may follow an employee home.</p>
            <p>
              Workplace chaplaincy recognizes that the person and the
              employee are not two separate people.
            </p>
            <p>
              CCCHAPS provides a confidential place where the human side of
              work and life can be acknowledged without turning every
              difficulty into a workplace process.
            </p>
          </div>
        </section>

        {/* Support for the whole organization */}
        <section className="mt-16 md:mt-20">
          <SectionHeading eyebrow="Whole organization">
            Support for the Whole Organization
          </SectionHeading>
          <p className="text-base text-slate-700 leading-relaxed mb-6">
            Leadership / Employee Support can serve:
          </p>
          <ul
            className="grid sm:grid-cols-2 gap-x-6 gap-y-3"
            data-testid="les-audience"
          >
            {AUDIENCE.map((t) => (
              <li key={t} className="flex items-start gap-3 text-slate-700">
                <Users
                  className="w-5 h-5 text-[#4A6FA5] shrink-0 mt-0.5"
                  strokeWidth={1.75}
                />
                <span className="leading-relaxed">{t}</span>
              </li>
            ))}
          </ul>
          <div className="mt-8 space-y-3 text-base text-slate-700 leading-relaxed">
            <p>Every role carries a different kind of responsibility.</p>
            <p>
              Every person may reach a point when they need someone outside
              the normal workplace structure to talk with.
            </p>
          </div>
        </section>

        {/* Closing */}
        <section className="mt-16 md:mt-20">
          <SectionHeading eyebrow="Space to talk">
            Someone to Talk With — Without Becoming Another Reporting
            Relationship
          </SectionHeading>
          <div className="space-y-5 text-base text-slate-700 leading-relaxed">
            <p>
              CCCHAPS workplace chaplaincy creates space for conversations
              that do not always fit naturally inside management, HR, EAP, or
              clinical care.
            </p>
            <p>Sometimes that conversation leads to another resource.</p>
            <p>
              Sometimes it leads to a difficult decision becoming clearer.
            </p>
            <p>
              Sometimes it simply gives a person the opportunity to say what
              they have been carrying.
            </p>
            <p className="font-medium text-slate-900">
              That is valuable care in itself.
            </p>
          </div>
        </section>

        {/* Final CTA */}
        <section
          className="mt-14 rounded-2xl bg-[#F0F4F8] border border-blue-100 p-6 md:p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
          data-testid="les-final-cta"
        >
          <div>
            <h3 className="font-heading text-xl font-medium text-slate-900">
              Ready to Talk?
            </h3>
            <p className="text-sm text-slate-600 mt-1 max-w-xl">
              If your organization is considering confidential chaplain
              support for executives, HR leaders, managers, or employees, we
              can discuss your workforce and the type of support structure
              that would fit your organization.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/schedule?type=employee_support"
              data-testid="les-final-cta-schedule"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B] whitespace-nowrap"
            >
              Request Leadership or Employee Support{" "}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </div>
    </article>
  );
}
